import { createElement, ReactNode } from 'react';
import CursiveSpan from '../components/CursiveSpan/CursiveSpan';
import SecondarySpan from '../components/SecondarySpan/SecondarySpan';
import { ANY, NONE } from './Constants';
import { getInfinitySymbol, getId, formatDate, formatToUTCDate } from './MiscUtils';
import { BrandAndMarketListItem, GuidIdAndLabel, GuidIdCodeAndLabel } from '../integrated-client/models/';

type FormatBlocksInCellFuncOpts = {
  value: any;
  fieldName?: string;
  fallbackValue?: string;
};

type FormatArrayToBlocksInCellOpts = {
  values: any[];
  fieldName?: string;
  fallbackValue?: string;
};

type FormatSecondaryValueIfFalseOpts = {
  item: any;
  isSecondaryFieldName: string;
  labelFieldName?: string;
  customChildren?: ReactNode;
  shouldSetKey?: boolean;
  isLast?: boolean;
  labelCodeFieldName?: string | null;
};

type FormatSecondaryValuesArrayIfFalseOpts = {
  items: any[];
  isSecondaryFieldName: string;
  labelFieldName?: string;
  fallbackValue?: string;
  cursive?: boolean;
};

class TableFormatter {
  static getFallBackValue = (value: string, cursive: boolean = true) => createElement(cursive ? CursiveSpan : 'span', null, value);

  static formatSimpleObjectsArray(values: Array<any>, fallbackValue: any = ANY, fieldName: string = 'label') {
    let result: any = TableFormatter.getFallBackValue(fallbackValue);
    if (values.length > 0) {
      result = values
        .reduce((acc: String[], obj: any) => {
          return [...acc, obj[fieldName]];
        }, [])
        .join(', ');
    }
    return createElement('span', null, result);
  }

  static formatStringsArray(values: Array<string>, fallbackValue: any = ANY) {
    let result: any = TableFormatter.getFallBackValue(fallbackValue);
    if (values?.length > 0) {
      result = values.join(', ');
    }
    return createElement('span', null, result);
  }

  static formatEffectiveDateRange(
    effectiveFrom: Date | string | undefined | null | object,
    effectiveTo: Date | string | undefined | null | object,
    fallbackValue: string = NONE
  ) {
    let result: string | null = null;
    let dateFromStr: string;
    let dateToStr: string;
    const dateFrom = typeof effectiveFrom === 'string' ? new Date(effectiveFrom) : effectiveFrom;
    const dateTo = typeof effectiveTo === 'string' ? new Date(effectiveTo) : effectiveTo;

    dateFromStr = !dateFrom ? getInfinitySymbol() : dateFrom instanceof Date ? dateFrom.toISOString().slice(0, 10) : dateFrom.toString();
    dateToStr = !dateTo ? getInfinitySymbol() : dateTo instanceof Date ? dateTo.toISOString().slice(0, 10) : dateTo.toString();
    result = `${dateFromStr} — ${dateToStr}`;

    if (dateFromStr === getInfinitySymbol() && dateToStr === getInfinitySymbol()) {
      result = null;
    }

    return result === null ? TableFormatter.getFallBackValue(fallbackValue) : createElement('span', null, result);
  }

  static formatSingleString(value: any, fallbackValue: string = NONE) {
    let result: string | null = null;

    if (value) {
      result = value.toString();
    }

    return result === null ? TableFormatter.getFallBackValue(fallbackValue) : createElement('span', null, result);
  }

  static formatAgencyGroupObject(value: GuidIdAndLabel, fallbackValue: string = NONE) {
    let result: string | null = null;

    if (value?.label) {
      result = value?.label;
    }

    return result === null ? TableFormatter.getFallBackValue(fallbackValue) : createElement('span', null, result);
  }

  static formatBlockInCell({ value, fieldName, fallbackValue = ANY }: FormatBlocksInCellFuncOpts): ReactNode | null {
    if (!value) return fallbackValue;
    let result: string | null = null;
    if (typeof value === 'object' && fieldName) {
      result = value[fieldName];
    } else {
      result = value;
    }
    return createElement('div', { key: getId() }, result);
  }

  static formatArrayToBlocksInCell({ values, fieldName, fallbackValue = ANY }: FormatArrayToBlocksInCellOpts): ReactNode | null {
    if (!values || !values.length) return fallbackValue;
    return values.map((value: any) => this.formatBlockInCell({ value, fieldName, fallbackValue }));
  }

  static formatSecondaryValueIfFalse(opts: FormatSecondaryValueIfFalseOpts) {
    const { item, isSecondaryFieldName, labelFieldName, customChildren, shouldSetKey, labelCodeFieldName, isLast = true } = opts;
    const field = item ? item[isSecondaryFieldName] : false;
    const isSecondary = field !== undefined && field === false;
    let children;
    if (item) {
      children = !labelFieldName
        ? customChildren
        : isLast
        ? labelCodeFieldName
          ? `(${item[labelCodeFieldName]}) ${item[labelFieldName]}`
          : item[labelFieldName]
        : labelCodeFieldName
        ? `(${item[labelCodeFieldName]}) ${item[labelFieldName]}, `
        : `${item[labelFieldName]}, `;
    } else if (!labelFieldName) {
      children = customChildren;
    }
    const props: any = { isSecondary, children };
    if (shouldSetKey) {
      props.key = getId();
    }
    return createElement(SecondarySpan, props);
  }

  static formatSecondaryValuesArrayIfFalse(opts: FormatSecondaryValuesArrayIfFalseOpts) {
    const { items, isSecondaryFieldName, labelFieldName, fallbackValue = ANY, cursive = true } = opts;
    if (!items.length || items.length === 0) return TableFormatter.getFallBackValue(fallbackValue, cursive);
    return items.reduce((acc: any[], item: any, index: number) => {
      const formatedComponent = TableFormatter.formatSecondaryValueIfFalse({
        item,
        isSecondaryFieldName,
        labelFieldName,
        isLast: index + 1 === items.length,
        shouldSetKey: true,
      });
      acc.push(formatedComponent);
      return acc;
    }, []);
  }

  static formatAgencyObject(value: GuidIdCodeAndLabel) {
    const newValue = `(${value.code}) ${value.label}`;
    return createElement('span' + '', null, newValue);
  }

  static formatBrandAndMarketObject(value: BrandAndMarketListItem) {
    return `${value.brand?.label} (${value.market?.label})`;
  }

  static getDateRanges(value: { item1: string; item2: string }[]) {
    return value.map((dateRange) => {
      return `${dateRange.item1.replaceAll('-', '/')} - ${dateRange.item2.replaceAll('-', '/')}`;
    });
  }

  static getDate(value: Date) {
    return formatDate(formatToUTCDate(value));
  }

  static getTimeRanges(value: { item1: string; item2: string }[]) {
    return value.map((timeRange) => {
      return `${timeRange.item1.slice(0, 5)} - ${timeRange.item2.slice(0, 5)}`;
    });
  }

  static getWeekDays(value: string[]) {
    return value.map((week) => week.slice(0, 3)).join(', ');
  }
  static formatAgeRange(value: { from: string; to: string }) {
    const newValue = `Age ${value.from} to ${value.to}`;
    return createElement('span' + '', null, newValue);
  }
}

export default TableFormatter;
